import { ChangeDetectorRef, Component, OnInit, inject } from '@angular/core';
import { ActivatedRoute } from '@angular/router';

import { loadStripe, type Stripe } from '@stripe/stripe-js';

import { ApiService } from '../../services/api.service';
import { environment } from '../../../environments/environment';
import { LogService } from '../../services/log.service';
import { AnalyticsService } from '../../services/analytics.service';

// Typage propre du plan
interface Plan {
  id: string;
  name: string;
  price: string;
  period: string;
}

@Component({
  selector: 'app-checkout',
  imports: [],
  templateUrl: './checkout.component.html',
})
export class CheckoutComponent implements OnInit {
  private log = inject(LogService);
  private analytics = inject(AnalyticsService);
  private route = inject(ActivatedRoute);
  private api = inject(ApiService);
  private cdr = inject(ChangeDetectorRef);

  planId: string | null = null;
  plan: Plan | null = null;

  isLoading = false;
  errorMessage: string | null = null;

  // Plans disponibles (peuvent venir plus tard du backend)
  private readonly planData: Record<string, Plan> = {
    starter: {
      id: 'starter',
      name: 'Starter',
      price: '29 €',
      period: '/ mois',
    },
    pro: { id: 'pro', name: 'Pro', price: '79 €', period: '/ mois' },
    business: {
      id: 'business',
      name: 'Business',
      price: '149 €',
      period: '/ mois',
    },
  };

  ngOnInit(): void {
    // On lit ?plan=xxx dans l’URL
    this.route.queryParamMap.subscribe((params) => {
      this.planId = params.get('plan');

      if (this.planId && this.planData[this.planId]) {
        this.plan = this.planData[this.planId];
        this.errorMessage = null;
      } else {
        this.plan = null;
        this.errorMessage = 'Le plan sélectionné est invalide ou introuvable.';
      }
      this.cdr.markForCheck();
    });
  }

  async proceedToPayment(): Promise<void> {
    if (!this.planId) return;

    // Vérification du mode en ligne
    if (!navigator.onLine) {
      this.errorMessage =
        'Vous devez être connecté à Internet pour effectuer un paiement.';
      return;
    }

    this.isLoading = true;
    this.errorMessage = null;

    // Appel au backend → création d'une session Stripe
    this.api.createCheckoutSession(this.planId).subscribe({
      next: async (session) => {
        this.log.debug('👉 Session Stripe reçue :', session);
        try {
          // Double vérification en ligne avant de charger Stripe
          if (!navigator.onLine) {
            this.errorMessage = 'Connexion Internet requise pour le paiement.';
            this.isLoading = false;
            this.cdr.markForCheck();
            return;
          }

          const stripe = (await loadStripe(environment.stripePublicKey)) as any;
          this.log.debug('👉 Stripe loaded :', stripe);

          if (!stripe) {
            this.errorMessage =
              'Erreur interne : impossible de charger Stripe.';
            this.isLoading = false;
            this.cdr.markForCheck();
            return;
          }

          this.analytics.trackAbonnement(this.planId || 'unknown');
          window.location.href = session.url;
        } catch (e: any) {
          this.log.error('Stripe redirect error:', e);
          // Si c'est une erreur réseau et qu'on est hors ligne, message plus clair
          if (!navigator.onLine) {
            this.errorMessage =
              "Impossible d'accéder au service de paiement hors ligne.";
          } else {
            this.errorMessage =
              'Une erreur est survenue lors de la redirection vers Stripe.';
          }
        }

        this.isLoading = false;
        this.cdr.markForCheck();
      },

      error: (err) => {
        this.log.error('🔥 ERREUR API :', err);
        this.errorMessage =
          err?.error?.message ||
          'Une erreur est survenue lors de la création de la session de paiement.';
        this.isLoading = false;
        this.cdr.markForCheck();
      },
    });
  }
}
